// ContextCard — the data vocabulary the card renders, pure (no JSX, no I/O).
//
// Promoted from sketches/context-card/contextCardTypes.ts. The sketch invented
// a flat `ContextItemKind` with a 4th `"upload"` kind; production does NOT get
// a 4th resource kind (Q3 resolution). Instead a still-ingesting upload is a
// TRANSIENT UI state that carries no resource id yet, modelled as its own
// variant of a discriminated sum and swapped by the host for a resolved
// `source` ContextItem once ingest finishes. So the card's items are a sum of
// two shapes — a resolved production `ComposerResourceRef`, or an in-flight
// upload — never a flag on one shape.

import type {
    ComposerResourceKind,
    ComposerResourceRef,
} from "../Composer/PowerComposer/types.js";
import type { IconName } from "../Icon/Icon.js";

// The two phases an in-flight upload passes through before it becomes a
// resolved `source`. Distinct labels so the card can say which is happening;
// both are transient and end when the host replaces the item with a resolved
// resource (or removes it on failure).
export type UploadPhase = "uploading" | "ingesting";

// A file the author dropped/picked that is still being ingested — a TRANSIENT
// UI state with a client-local staging id, NOT a resource id. There is no
// production resource kind for this; when ingest finishes the host swaps it for
// a resolved `source` ContextItem.
export type PendingUpload = {
    readonly id: string;
    readonly filename: string;
    readonly phase: UploadPhase;
};

// One thing grounding the conversation, as a sum discriminated by `_tag`:
// either a resolved resource (its production ref) or a still-ingesting upload.
// The card never sees a half-populated item — each variant carries exactly its
// own data.
export type ResolvedContextItem = {
    readonly _tag: "resource";
    readonly ref: ComposerResourceRef;
};
export type PendingContextItem = {
    readonly _tag: "upload";
    readonly upload: PendingUpload;
};
export type ContextItem = ResolvedContextItem | PendingContextItem;

// The id used for keying, dedup, and remove — regardless of variant. A
// resource uses its resource id; a pending upload uses its staging id (the two
// id-spaces never collide, which is correct: an in-flight upload is not yet a
// pickable resource).
export const contextItemId = (item: ContextItem): string =>
    item._tag === "resource" ? item.ref.id : item.upload.id;

// Is this pickable source ALREADY grounded (as a resolved resource)? Drives
// the pick menu's disabled-with-check row — error prevention (Nielsen #5):
// picking the same source twice must read as impossible, not silently no-op. A
// pending upload never matches a pickable id, so an in-flight file never
// disables its eventual source row spuriously.
export const isSourceInContext = (
    items: readonly ContextItem[],
    sourceId: string,
): boolean =>
    items.some((item) => item._tag === "resource" && item.ref.id === sourceId);

// One glyph per resource kind — mirrors ResourceMentionChip's `kindGlyph`.
// `package` borrows the folder glyph (no dedicated icon yet — a flagged
// stand-in, tracked for a design pass alongside the source/material likeness).
export const kindGlyph = (kind: ComposerResourceKind): IconName => {
    switch (kind) {
        case "source":
            return "source";
        case "material":
            return "material";
        case "package":
            return "folder";
        case "file":
            return "file";
    }
};

// The glyph for any item — the upload glyph while in-flight, else the resource
// kind glyph.
export const contextItemGlyph = (item: ContextItem): IconName =>
    item._tag === "upload" ? "upload" : kindGlyph(item.ref.kind);

// The composer's controlled `mentions` list, projected into the card's `items`
// shape: every mention IS a resolved-resource context item. In-flight uploads
// (a transient UI state with no resource id yet) are NOT mentions, so a host
// concatenates them separately — see WorkspaceLanding. This is the structural
// proof that the card and the `@`-mention menu ground the SAME set: one is a
// pure projection of the other.
export const mentionsToContextItems = (
    mentions: readonly ComposerResourceRef[],
): readonly ContextItem[] => mentions.map((ref) => ({ _tag: "resource", ref }));
